/**
 * Global counter bumped when viewport geometry changes in ways that often leave
 * iOS/WebKit stale on env(safe-area-inset-*). Consumers use useSyncExternalStore
 * so all roles share one listener set (no duplicate window handlers per component).
 *
 * Orientation settle (2026-07-28): debounced via orientationSettle.js so remounts
 * land after Safari finishes chrome + insets (not mid-animation).
 */

import {
  scheduleOrientationSettle,
  onOrientationSettled,
  isOrientationSettling,
  ORIENTATION_SETTLE_MS,
  __resetOrientationSettleForTests,
} from './orientationSettle';

const isBrowser = typeof window !== 'undefined';

export { isOrientationSettling, ORIENTATION_SETTLE_MS, scheduleOrientationSettle };

let epoch = 0;
const listeners = new Set();
let rafId = 0;
let installed = false;
let settleHookUnsub = null;
let onOrient = null;
let onMql = null;
let mqlRef = null;
let onPageshow = null;

function flushBump() {
  epoch += 1;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

function scheduleBumpImmediate() {
  if (!isBrowser) return;
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(flushBump);
}

function installListenersOnce() {
  if (!isBrowser || installed) return;
  installed = true;
  settleHookUnsub = onOrientationSettled(flushBump);
  onOrient = () => scheduleOrientationSettle();
  window.addEventListener('orientationchange', onOrient);
  mqlRef = window.matchMedia?.('(orientation: portrait)') ?? null;
  onMql = () => scheduleOrientationSettle();
  if (mqlRef?.addEventListener) mqlRef.addEventListener('change', onMql);
  else if (mqlRef?.addListener) mqlRef.addListener(onMql);
  onPageshow = (e) => {
    if (e.persisted) scheduleBumpImmediate();
  };
  window.addEventListener('pageshow', onPageshow);
}

/**
 * Ensure orientation listeners exist even before React mounts a subscriber
 * (so safe-top floor still settles on rotate at the login screen).
 */
export function ensureViewportSafeAreaLayoutEpochInstalled() {
  installListenersOnce();
}

/** @internal test helper — full teardown so suites don't stack listeners */
export function __resetViewportSafeAreaLayoutEpochForTests() {
  if (isBrowser && installed) {
    if (onOrient) window.removeEventListener('orientationchange', onOrient);
    if (mqlRef && onMql) {
      if (mqlRef.removeEventListener) mqlRef.removeEventListener('change', onMql);
      else if (mqlRef.removeListener) mqlRef.removeListener(onMql);
    }
    if (onPageshow) window.removeEventListener('pageshow', onPageshow);
  }
  if (settleHookUnsub) {
    settleHookUnsub();
    settleHookUnsub = null;
  }
  __resetOrientationSettleForTests();
  cancelAnimationFrame(rafId);
  rafId = 0;
  epoch = 0;
  listeners.clear();
  installed = false;
  onOrient = null;
  onMql = null;
  mqlRef = null;
  onPageshow = null;
}

/**
 * @param {() => void} onStoreChange
 * @returns {() => void} unsubscribe
 */
export function subscribeViewportSafeAreaLayoutEpoch(onStoreChange) {
  installListenersOnce();
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function getViewportSafeAreaLayoutEpoch() {
  return epoch;
}
